<template>
  <b-overlay :show="mapLoading" bg-color="transparent" blur="2px">
    <template #overlay>
      <div v-if="mapLoading" class="text-center py-3">
        <img src="@/assets/lottie/plant.gif" alt="Loading..." style="width: 40px;" />
      </div>
    </template>

    <b-card no-body class="rounded-xl mb-5">
      <b-card-header
        class="border-2 border-bottom px-5 pt-0 pb-3 d-flex align-items-center justify-content-between"
        v-if="title"
      >
        <h3>{{ title }}</h3>

        <!-- LAYER TOGGLE LIST -->
        <div v-if="layers.length">
          <b-dropdown text="Layer Visibility" right size="sm" variant="outline-primary">
            <b-dropdown-item
              v-for="(layer, index) in layers"
              :key="layer.id"
              @click="toggleLayerVisibility(layer.id)"
            >
              <i
                :class="[
                  'mr-2',
                  layer.visible ? 'ri-eye-fill text-success' : 'ri-eye-off-fill text-muted'
                ]"
              ></i>
              {{ layer.name }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </b-card-header>

      <b-card-body class="p-0">
        <div class="map-wrapper" style="height: 400px; width: 100%; position: relative;">
          <template v-if="!isMapLoaded">
            <div
              class="d-flex align-items-center justify-content-center"
              style="height: 100%; width: 100%; background: rgba(0,0,0,0.2); border-radius: 10px;"
            >
              <div
                class="buka-map-btn text-center py-3 px-4 bg-light text-primary rounded shadow-sm"
                style="cursor: pointer;"
                @click="loadMap"
              >
                Buka Maps
              </div>
            </div>
          </template>

          <div v-else ref="mapContainer" style="height: 100%; width: 100%; border-radius: 10px;"></div>
        </div>
      </b-card-body>
    </b-card>
  </b-overlay>
</template>

<script>
import JSZip from "jszip";

export default {
  name: "multi-polygon-loader",
  props: {
    title: { type: String, default: null },
    coordinate_data: {
      type: Object,
      default: function () {
        return { latitude: -7.024947, longitude: 110.414672 };
      },
    },
    polygons_data: {
      type: Array,
      default: function () {
        return [];
      },
    }, // Array of { name, url, ext, layer_color }
  },
  data() {
    return {
      mapLoading: false,
      maps: null,
      isMapLoaded: false,
      layers: [],
    };
  },
  methods: {
    async loadMap() {
      this.mapLoading = true;
      this.isMapLoaded = true;

      if (
        !this.coordinate_data
      ) {
        console.warn("Invalid coordinate_data:", this.coordinate_data);
        
        this.mapLoading = false;
        return;
      }else if( typeof this.coordinate_data.latitude !== "number" || typeof this.coordinate_data.longitude !== "number"){
        this.coordinate_data.latitude = Number(this.coordinate_data.latitude);
        this.coordinate_data.longitude = Number(this.coordinate_data.longitude);
      }
      console.log("coordinate_data:", this.coordinate_data);
      
      await this.$nextTick();
      await this.initializeMap();

      this.mapLoading = false;
    },

    async initializeMap() {
      const { latitude, longitude } = this.coordinate_data;

      this.maps = new mapboxgl.Map({
        accessToken: this.$_config.apikey.mapbox,
        container: this.$refs.mapContainer,
        style: "mapbox://styles/mapbox/satellite-streets-v12",
        center: [longitude, latitude],
        zoom: 10,
        projection: "globe",
      });

      this.maps.addControl(new mapboxgl.NavigationControl());
      this.maps.addControl(new mapboxgl.FullscreenControl());

      new mapboxgl.Marker({ color: "red" }).setLngLat([longitude, latitude]).addTo(this.maps);

      this.maps.on("load", async () => {
        for (let i = 0; i < this.polygons_data.length; i++) {
          const poly = this.polygons_data[i];
          await this.loadPolygonLayer(poly, i);
        }

        if (this.polygons_data.length > 0 && this.maps.getSource("polygon-layer-0")) {
          const firstLayer = this.maps.getSource("polygon-layer-0")._data;
          if (firstLayer && firstLayer.features && firstLayer.features.length) {
            const bounds = new mapboxgl.LngLatBounds();
            firstLayer.features.forEach((feature) => {
              const geom = feature.geometry;
              if (geom && geom.coordinates) {
                const flatCoords = this.flattenCoords(geom.coordinates);
                flatCoords.forEach((coord) => {
                  if (Array.isArray(coord) && coord.length >= 2 && !isNaN(coord[0]) && !isNaN(coord[1])) {
                    bounds.extend([coord[0], coord[1]]);
                  }
                });
              }
            });
            this.maps.fitBounds(bounds, { padding: 50 });
          }
        }
      });
    },

    async loadPolygonLayer(polygon, index) {
      try {
        const res = await fetch(polygon.url);
        const blob = await res.blob();
        const ext = polygon.ext.toLowerCase();

        var geojson = null;
        if (ext === "kml") {
          const text = await blob.text();
          const parser = new DOMParser();
          const kmlDoc = parser.parseFromString(text, "application/xml");
          geojson = window.toGeoJSON.kml(kmlDoc);
        } else if (ext === "kmz") {
          const arrayBuffer = await blob.arrayBuffer();
          const zip = await JSZip.loadAsync(arrayBuffer);
          const kmlFile = Object.keys(zip.files).find(function (f) {
            return f.endsWith(".kml");
          });
          const kmlText = await zip.files[kmlFile].async("string");
          const parser = new DOMParser();
          const kmlDoc = parser.parseFromString(kmlText, "application/xml");
          geojson = window.toGeoJSON.kml(kmlDoc);
        }

        if (geojson && geojson.features && geojson.features.length) {
          const layerId = "polygon-layer-" + index;
          this.addGeoJSONToMap(layerId, geojson, polygon.name, polygon.layer_color);
        }
      } catch (err) {
        console.error("Failed to load polygon:", err);
      }
    },

    addGeoJSONToMap(layerId, geojson, name, color) {
      if (this.maps.getSource(layerId)) return;

      this.maps.addSource(layerId, { type: "geojson", data: geojson });

      this.maps.addLayer({
        id: layerId + "-fill",
        type: "fill",
        source: layerId,
        paint: {
          "fill-color": color || this.randomColor(),
          "fill-opacity": 0.4,
        },
      });

      this.maps.addLayer({
        id: layerId + "-outline",
        type: "line",
        source: layerId,
        paint: {
          "line-color": "#ffffff",
          "line-width": 1.5,
        },
      });

      // Register this layer
      this.layers.push({
        id: layerId,
        name: name || "Layer " + (this.layers.length + 1),
        visible: true,
      });

      // Adjust view
      const bounds = new mapboxgl.LngLatBounds();

      geojson.features.forEach((feature) => {
        if (
          feature.geometry &&
          Array.isArray(feature.geometry.coordinates)
        ) {
          const coords = feature.geometry.coordinates.flat(2);
          coords.forEach((coord) => {
            if (Array.isArray(coord) && coord.length >= 2) {
              bounds.extend([coord[0], coord[1]]);
            }
          });
        }
      });

      if (!bounds.isEmpty()) {
        this.maps.fitBounds(bounds, { padding: 50 });
      }
    },

    toggleLayerVisibility(layerId) {
      const layer = this.layers.find(function (l) {
        return l.id === layerId;
      });
      if (!layer) return;

      const visibility = layer.visible ? "none" : "visible";
      this.maps.setLayoutProperty(layerId + "-fill", "visibility", visibility);
      this.maps.setLayoutProperty(layerId + "-outline", "visibility", visibility);

      layer.visible = !layer.visible;
    },

    randomColor() {
      const colors = [
        "#e6194b",
        "#3cb44b",
        "#ffe119",
        "#4363d8",
        "#f58231",
        "#911eb4",
        "#46f0f0",
        "#f032e6",
      ];
      return colors[Math.floor(Math.random() * colors.length)];
    },
    flattenCoords(coords) {
      var flat = [];
      (function recurse(c) {
        if (typeof c[0] === "number") {
          flat.push(c);
        } else {
          c.forEach(recurse);
        }
      })(coords);
      return flat;
    },
  },
};
</script>

<style scoped>
.map-wrapper {
  border-radius: 10px;
  overflow: hidden;
}
.map-placeholder {
  background: url('https://api.mapbox.com/styles/v1/mapbox/satellite-v9/static/110.4, -7.02,9,0/800x400?access_token=YOUR_MAPBOX_KEY') center/cover no-repeat;
  filter: blur(5px) brightness(0.8);
}
</style>



// WEBPACK FOOTER //
// src/base/component/multi-polygon-loader.vue